<template>
  <span>

    <!-- Render a group of conditions -->
    <span class="x-filter-group">
      <transition-group name="x-filter-transition">
        <x-filter-condition
          v-for="(condition, index) in getAllConditions()"
          :key="condition.attribute + index"
          :condition="condition"
          :operator="getOperator()"
          :isLast="index == getAllConditions().length - 1"
          @removeCondition="removeCondition"
        />
      </transition-group>
    </span>

    <!-- The add condition button and menu -->
    <q-btn-dropdown
      class="x-filter-add-btn"
      :label="$t('ui.xfilters.add_filter')"
      no-ripple
      no-caps
    >
      <q-list class="x-filter-add-list" link ref="deflist">
        <q-item
          v-for="(definition, index) in filterDefinitions"
          :key="index"
          class="x-filter-add-list-item"
          @click.native="openAddConditionModal(definition)"
          v-close-overlay
          :separator="definition.separator ? true : false"
          :disabled="definition.disabled ? true : false"
          >{{ definition.label }}</q-item
        >
      </q-list>
    </q-btn-dropdown>

    <!-- The add condition modal -->
    <x-filter-modal
      :filterDefinition="filterDefinition"
      :isOpen="filterModalOpen"
      :key="modalKey"
      @addCondition="addCondition"
    />

    <!-- Delete confirmation modal -->
    <modal
      :open="confirmationModalOpen"
      :title="$t('ui.global.warning')"
      type="warning"
      :showCancelForWarning="true"
      saveText="ui.xfilters.confirm_removal"
      @save="deleteConfirmed"
      @cancel="deleteCancelled"
    >
      <p>{{ $t('ui.xfilters.delete_filter_warning') }}</p>
      <p>{{ $t('ui.xfilters.delete_filter_confirmation') }}</p>
    </modal>
  </span>
</template>

<script>
import XFilterCondition from "@/components/helpers/DataInput/XFilterCondition"
import XFilterModal from "@/components/helpers/DataInput/XFilterModal"
import Modal from "@/components/helpers/Layout/NewModal"
import moment from "moment"

export default {
  components: { XFilterCondition, XFilterModal, Modal },
  props: {
    group: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    filterDefinitions: {
      type: Array,
      required: true,
    }
  },
  data() {
    return {
      workingGroup: null,
      filterDefinition: {},
      filterModalOpen: false,
      confirmationModalOpen: false,
      confirmationParameters: null,
      modalKey: 0
    }
  },
  beforeMount() {
    this.workingGroup = _.cloneDeep(this.group)

    this.convertConditionsFromCanonical(this.workingGroup)

    if (this.workingGroup.conditions) {
      //this.updateGroup()
    }
  },
  mounted() {
  },
  methods: {
    getAllConditions() {
      return _.get(this.workingGroup, 'conditions', [])
    },
    getCondition(id) {

      // Find the filter condition that matches the given id
      let conditions = this.getAllConditions()

      let found = null
      conditions.forEach((condition, idx) => {
        if (condition.id == id) {
          found = condition
          found.index = idx
        }
      })

      return found
    },
    getConditionByAttribute(attribute) {

      // Find the first filter condition that matches the given attribute
      let conditions = this.getAllConditions()

      let found = null
      conditions.forEach((condition, idx) => {
        if (condition.attribute == attribute) {
          found = condition
          found.index = idx
        }
      })

      return found
    },
    getOperator() {
      return _.get(this.workingGroup, 'operator', 'and')
    },
    isSet(value) {
      if (Array.isArray(value)) {
        if (value.length == 0) {
          return false
        }
        return value.reduce((acc, cur) => {
          return acc || this.isSet(cur)
        })
      } else {
        return typeof value !== 'undefined' && value !== '' && value !== null
      }
    },
    openAddConditionModal(definition) {
      if (definition.disabled) {
        return
      }
      this.filterDefinition = definition
      this.filterModalOpen = true
      this.modalKey++
    },
    async addCondition(definition, data, hasNone = false) {

      // Basic condition fields
      let condition = {
        id: this.generateUuidv4(),
        depends_on: definition.depends_on,
        attribute: definition.attribute,
        predicate: data.predicate || definition.predicate,
        value_id: data.value_id,
        value: data.value,
        definition: definition,
      }

      // Add any static fields defined by the definition
      if (typeof definition.static == 'object' && definition.static != null) {
        condition = Object.assign(condition, definition.static)
      }

      if (definition.type == 'date') {
        if (data.dateRange != 'custom') {
          condition.predicate = 'in_range'
          condition.value = data.dateRange
        } else {
          condition.value = [
            data.value ? moment(data.value).startOf('day').format('YYYY-MM-DD') : null,
            // We add one day to the end date because we use the "lt" predicate,
            // but the end date should be included.
            data.value2 ? moment(data.value2).add(1, 'day').startOf('day').format('YYYY-MM-DD') : null,
          ]
        }
      }

      // Add type-specific fields
      if (hasNone) {
        condition.predicate = 'has_none'
        condition.value = null
      }

      // Only add condition if it includes actionable values
      if (condition.predicate == 'has_none' || this.isSet(condition.value)) {

        // First remove any existing condition for the same attribute
        let prevCondition = this.getConditionByAttribute(condition.attribute)
        let prevRemoved = true
        if (prevCondition) {
          prevRemoved = await this.removeCondition(prevCondition.id, false)
        }

        // Then add new condition
        if (prevRemoved) {        
          this.workingGroup.conditions.push(condition)
          this.updateGroup()
        }
      }
    },
    getDependantConditions(condition) { 
      let dependants = []
      this.workingGroup.conditions.forEach((cond) => {
        if (cond.depends_on && condition.id && cond.depends_on == condition.id) {
          dependants.push(cond)
        }
      })
      return dependants
    },
    async removeCondition(id, updateParent = true, confirmDependancies = true) {

      let condition = this.getCondition(id)
      if (condition !== null) {

        // Get a list of any dependant conditions
        let dependantConditions = this.getDependantConditions(condition)

        // If there are some dependencies, then ask for confirmation
        let confirmed = true
        if (dependantConditions.length > 0 && confirmDependancies) {         
          confirmed = await this.confirmDelete()
        }

        if (confirmed) {
          // First, remove the requested condition
          // (We need to do this now so that the index is valid)
          this.workingGroup.conditions.splice(condition.index, 1)

          // Then remove any dependant conditions
          // If any have their own dependancies, then we assume confirmation
          if (dependantConditions.length > 0) {
            for (const dependant of dependantConditions) {
              await this.removeCondition(dependant.id, false, false)
            }
          }

          if (updateParent) {
            this.updateGroup()
          }
        }

        return confirmed
      }
    },
    async confirmDelete() {
      // Open the confirmation modal and return a Promise
      // which is resolved according to the confirmation choice
      this.confirmationModalOpen = true
      return new Promise((resolve, reject) => {
        this.confirmationParameters = {
          resolve: resolve,
          reject: reject,
        }
      })
    },
    deleteConfirmed() {
      this.confirmationModalOpen = false
      if (this.confirmationParameters != null) {
        this.confirmationParameters.resolve(true)
      }
      this.confirmationParameters = null
    },
    deleteCancelled() {
      this.confirmationModalOpen = false
      if (this.confirmationParameters != null) {
        this.confirmationParameters.resolve(false)
      }
      this.confirmationParameters = null
    },
    // The cononical filter conditions (those saved to db), specify date
    // ranges (from/to) as seperate conditions (gt/lt). But internall,
    // these are combined as a single condition, because that is how it's
    // displayed in the UI
    convertConditionsToCanonical(group) {

      let conditions = []

      if (Array.isArray(group.conditions) == false) {
        group.conditions = []
      }

      group.conditions.forEach((condition) => {

        let modCondition = _.cloneDeep(condition)

        if (modCondition.definition.type == 'date' && modCondition.predicate != 'in_range') {
          // Split condition into two and rename attribute
          let secondCondition = _.cloneDeep(modCondition)
          modCondition.attribute = modCondition.attribute
          modCondition.predicate = 'gt'
          modCondition.value = modCondition.value[0]
          secondCondition.attribute = secondCondition.attribute
          secondCondition.predicate = 'lt'
          secondCondition.value = secondCondition.value[1]

          if (modCondition.value != null && secondCondition.value != null) {
            conditions.push(secondCondition)
          } else {
            if (modCondition.value == null) {
              modCondition = secondCondition
            }
          }
        }
        conditions.push(modCondition)
      })

      group.conditions = conditions
    },
    // See convertConditionsToCanonical() above.
    convertConditionsFromCanonical(group) {

      let conditions = []
      let dateConditions = {}

      if (Array.isArray(group.conditions) == false) {
        group.conditions = []
      }

      group.conditions.forEach((condition) => {

        let modCondition = _.cloneDeep(condition)

        if (condition.definition.type == 'date' && condition.predicate != 'in_range') {

          if (Object.keys(dateConditions).includes(condition.attribute) == false) {
            modCondition.value = [null, null]
            modCondition.predicate = null
            dateConditions[modCondition.attribute] = modCondition
          }

          if (condition.predicate == 'gt') {
            dateConditions[condition.attribute].value[0] = condition.value
          }
          if (condition.predicate == 'lt') {
            dateConditions[condition.attribute].value[1] = condition.value
          }

        } else {
          conditions.push(modCondition)
        }
      })

      Object.values(dateConditions).forEach((dateCondition) => {
        conditions.push(dateCondition)
      })

      group.conditions = conditions
    },
    updateGroup() {
      let toUpdate = _.cloneDeep(this.workingGroup)
      this.convertConditionsToCanonical(toUpdate)
      this.$emit('updateGroup', toUpdate, this.index)
    }
  },
}
</script>

<style lang="sass" scoped>
</style>


// WEBPACK FOOTER //
// XFilterGroup.vue?31f84355